import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Handshake, Calendar, MapPin, Users, Star, X, Trash2, Edit2, MessageCircle, Send, Pencil, Flame } from "lucide-react";
import { useModal } from '../../context/ModalContext';
import { motion } from "framer-motion";
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../utils/supabaseClient';
import { awardPoints } from '../../utils/pointsService';
import { AvatarDisplay } from "../../utils/avatars";
import { SECTION_THEME } from '../../utils/sectionTheme';
import { SPORT_CATEGORY } from '../../utils/eventCategories';
import { getCardImageBadgeStyle } from '../../utils/cardBadgeStyles';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import LoginPromptModal from '../common/LoginPromptModal';

// Format comment timestamp: relative for recent, date for older
function formatCommentTime(isoString) {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '剛剛';
  if (diffMin < 60) return `${diffMin}分鐘前`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} 小時前`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} 天前`;
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

export default function EventCard({ event, currentUserId, onJoin, userFavorites = [], onFavoriteToggle, onEventCancel, onEventLeave, onEventEdit, onParticipantStatusChange, isDetailsPage = false, hideCategoryTag = false, primaryTagOverride, isPopular = false, isRecommended = false, recommendationType }) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const { showAlert, showConfirm } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState(event.comments || []);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isFavorited, setIsFavorited] = useState(userFavorites.includes(event._id));
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  // 從 event props 推導參與狀態（由 useEvents + Realtime 保持最新）
  const myStatus = event.participantStatuses?.[currentUserId] ?? null;
  const displayStartTime = event.startTime || event.time || '';
  const displayEndTime = event.endTime || '';
  const formattedTime = displayStartTime && displayEndTime
    ? `${displayStartTime} - ${displayEndTime}`
    : displayStartTime;

  useEffect(() => { setIsFavorited(userFavorites.includes(event._id)); }, [userFavorites, event._id]);
  useEffect(() => { setComments(event.comments || []); }, [event.comments]);


  const handleAddComment = async () => {
    if (event.status === 'cancelled') return;
    if (!currentUserId) {
      setIsLoginPromptOpen(true);
      return;
    }

    const content = newComment.trim();
    if (!content) return;

    try {
      const { data: createdComment, error } = await supabase
        .from('event_comments')
        .insert({
          event_id: event._id,
          user_id: currentUserId,
          content
        })
        .select('*, profiles(name, avatar)')
        .single();

      if (error) throw error;

      setComments(prev => [...prev, createdComment]);
      setNewComment('');
      awardPoints('comment', event._id);
    } catch (error) {
      console.error('留言失敗:', error);
      showAlert('錯誤', '留言失敗，請稍後再試');
    }
  };

  const handleEditComment = async (commentId) => {
    const content = editingContent.trim();
    if (!content) return;
    try {
      const { error } = await supabase
        .from('event_comments')
        .update({ content })
        .eq('id', commentId);
      if (error) throw error;
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, content } : c));
      setEditingCommentId(null);
      setEditingContent('');
    } catch (err) {
      console.error('編輯留言失敗:', err);
      showAlert('錯誤', '編輯失敗，請稍後再試');
    }
  };

  const handleDeleteComment = async (commentId) => {
    showConfirm('確認刪除', '確定要刪除這則留言嗎？', async () => {
      try {
        const { error } = await supabase
          .from('event_comments')
          .delete()
          .eq('id', commentId);
        if (error) throw error;
        setComments(prev => prev.filter(c => c.id !== commentId));
      } catch (err) {
        console.error('刪除留言失敗:', err);
        showAlert('錯誤', '刪除失敗，請稍後再試');
      }
    });
  };

  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleJoin = async () => {
    if (!currentUserId) { setIsLoginPromptOpen(true); return; }
    if (myStatus === 'pending') { showAlert('提示', '您的報名申請正在等待主辦人審核中'); return; }
    if (myStatus === 'approved' || (event.participants && event.participants.includes(currentUserId))) {
      showAlert('提示', '你已經是這個活動的參加者了'); return;
    }

    const isSports = event.category === SPORT_CATEGORY;
    const confirmMessage = isSports
      ? `確定要報名「${event.title}」嗎？\n\n報名成功後即可直接參加！`
      : `確定要報名「${event.title}」嗎？\n\n送出後需等待主辦人審核，審核通過後才算正式參加。`;

    showConfirm('確認報名', confirmMessage, async () => {
      setIsLoading(true);
      try {
        const targetStatus = isSports ? 'approved' : 'pending';
        const { error: insertError } = await supabase
          .from('event_participants')
          .upsert({ event_id: event._id, user_id: currentUserId, status: targetStatus }, { onConflict: 'event_id,user_id' });

        if (insertError) throw insertError;

        if (isSports) {
          queryClient.setQueryData(['events', currentUserId], (oldData) => {
            if (!oldData || !oldData.events) return oldData;
            return {
              ...oldData,
              events: oldData.events.map(e => {
                if (e._id === event._id) {
                  return {
                    ...e,
                    participants: [...(e.participants || []), currentUserId],
                    participantStatuses: {
                      ...(e.participantStatuses || {}),
                      [currentUserId]: 'approved'
                    }
                  };
                }
                return e;
              })
            };
          });
          queryClient.invalidateQueries({ queryKey: ['events'] });
          showAlert('成功', '報名成功！🎉');
        } else {
          showAlert('成功', '報名申請已送出！等待主辦人審核後即可參加 🎉');
        }

        awardPoints('join_event', event._id);
        if (onJoin) await onJoin(event._id);
      } catch (error) {
        console.error('報名失敗:', error);
        showAlert('錯誤', '報名失敗，請重試');
      } finally {
        setIsLoading(false);
      }
    });
  };

  const isEventOwner = event.organizer_id === currentUserId;
  const [pendingApplicants, setPendingApplicants] = useState([]);

  // Load pending applicants for organizer
  useEffect(() => {
    if (!isEventOwner || !event._id) return;
    const loadPending = async () => {
      const { data } = await supabase
        .from('event_participants')
        .select('user_id, profiles(name, avatar)')
        .eq('event_id', event._id)
        .eq('status', 'pending');
      setPendingApplicants(data || []);
    };
    loadPending();
  }, [isEventOwner, event._id]);

  // Calculate total participants including organizer (only count organizer if they exist)
  const totalParticipants = (event.participants ? event.participants.length : 0) + (event.organizer ? 1 : 0);

  // Phase 2.1: 分頁強調色（運動綠／活動橘），依 event.category 判斷所屬分頁
  const sectionTheme = event.category === SPORT_CATEGORY ? SECTION_THEME.sports : SECTION_THEME.events;

  const resolvedRecommendationType = recommendationType || event.recommendationType || event.recommendation?.recommendation_type || 'official';
  const showRecommendedBadge = isRecommended || (isDetailsPage && Boolean(event.recommendation));
  const showTrendingBadge = isPopular || (isDetailsPage && event.isTrending);
  const isPartnerRecommendation = resolvedRecommendationType === 'partner';
  const recommendationLabel = isDetailsPage
    ? (isPartnerRecommendation ? 'JoinU 品牌合作活動' : '官方推薦活動')
    : (isPartnerRecommendation ? '合作' : '推薦');
  const trendingLabel = isDetailsPage ? '近期熱門活動' : '熱門';


  const handleUserClick = (user) => {
    if (event.category === SPORT_CATEGORY) return;
    const userId = user._id || user.id;
    if (userId) {
      navigate(`/user/${userId}`);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!currentUserId) {
      setIsLoginPromptOpen(true);
      return;
    }

    try {
      if (isFavorited) {
        await supabase
          .from('favorite_events')
          .delete()
          .match({ user_id: currentUserId, event_id: event._id });
        setIsFavorited(false);
      } else {
        await supabase
          .from('favorite_events')
          .insert({ user_id: currentUserId, event_id: event._id });
        setIsFavorited(true);
      }

      if (onFavoriteToggle) {
        onFavoriteToggle(event._id, { isFavorited: !isFavorited });
      }
    } catch (error) {
      console.error("收藏失敗:", error);
    }
  };

  const handleCancelEvent = async () => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ status: 'cancelled' })
        .eq('id', event._id)
        .eq('organizer_id', currentUserId); // security check

      if (error) throw error;

      if (onEventCancel) {
        await onEventCancel(event._id);
      }
      showAlert("成功", "活動已取消");
    } catch (error) {
      console.error("取消活動錯誤:", error);
      showAlert("錯誤", "取消活動失敗，請重試");
    }
    setShowCancelDialog(false);
  };

  const handleLeaveEvent = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('event_participants')
        .delete()
        .match({ event_id: event._id, user_id: currentUserId });

      if (error) throw error;

      if (onEventLeave) {
        await onEventLeave(event._id);
      }
      showAlert("成功", "已退出活動");
    } catch (error) {
      console.error("退出失敗:", error);
      showAlert("錯誤", "退出失敗，請重試");
    } finally {
      setIsLoading(false);
    }
  };

  // Organizer: approve or reject a pending participant
  const handleApproveParticipant = async (applicantId, applicantName) => {
    try {
      const applicantDetail = pendingApplicants.find(p => p.user_id === applicantId);
      const { data, error } = await supabase
        .from('event_participants')
        .update({ status: 'approved' })
        .eq('event_id', event._id)
        .eq('user_id', applicantId)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("更新失敗，可能您不是此活動的建立者或資料庫缺少權限 (RLS)");
      }

      // 通知由 DB trigger (trg_notify_event_review) 自動發送
      setPendingApplicants(prev => prev.filter(p => p.user_id !== applicantId));

      // 樂觀更新 (Optimistic Update)
      queryClient.setQueryData(['events', currentUserId], (oldData) => {
        if (!oldData || !oldData.events) return oldData;
        return {
          ...oldData,
          events: oldData.events.map(e => {
            if (e._id === event._id) {
              const newParticipants = [...(e.participants || []), applicantId];
              const newParticipantDetails = [...(e.participantDetails || [])];
              if (applicantDetail && !newParticipantDetails.some(p => p._id === applicantId)) {
                newParticipantDetails.push({
                  _id: applicantId,
                  id: applicantId,
                  profile: applicantDetail.profiles
                });
              }
              return {
                ...e,
                participants: Array.from(new Set(newParticipants)),
                participantDetails: newParticipantDetails,
                participantStatuses: {
                  ...(e.participantStatuses || {}),
                  [applicantId]: 'approved'
                }
              };
            }
            return e;
          })
        };
      });

      // 立即強制刷新 events 快取，確保主辦人的 UI 同步更新
      queryClient.invalidateQueries({ queryKey: ['events'] });
      if (onParticipantStatusChange) onParticipantStatusChange();
      showAlert('成功', `已同意 ${applicantName} 參加活動！`);
    } catch (err) {
      console.error('審核失敗:', err);
      showAlert('錯誤', err.message || '審核失敗，請稍後再試');
    }
  };

  const handleRejectParticipant = async (applicantId, applicantName) => {
    try {
      const { data, error } = await supabase
        .from('event_participants')
        .update({ status: 'rejected' })
        .eq('event_id', event._id)
        .eq('user_id', applicantId)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("更新失敗，可能您不是此活動的建立者或資料庫缺少權限 (RLS)");
      }

      // 通知由 DB trigger (trg_notify_event_review) 自動發送
      setPendingApplicants(prev => prev.filter(p => p.user_id !== applicantId));

      // 樂觀更新 (Optimistic Update)
      queryClient.setQueryData(['events', currentUserId], (oldData) => {
        if (!oldData || !oldData.events) return oldData;
        return {
          ...oldData,
          events: oldData.events.map(e => {
            if (e._id === event._id) {
              return {
                ...e,
                participantStatuses: {
                  ...(e.participantStatuses || {}),
                  [applicantId]: 'rejected'
                }
              };
            }
            return e;
          })
        };
      });

      // 立即強制刷新 events 快取
      queryClient.invalidateQueries({ queryKey: ['events'] });
      if (onParticipantStatusChange) onParticipantStatusChange();
      showAlert('提示', `已婉拒 ${applicantName} 的報名。`);
    } catch (err) {
      console.error('婉拒失敗:', err);
      showAlert('錯誤', err.message || '婉拒失敗，請稍後再試');
    }
  };

  const renderActionButtons = () => (
    <div style={{ paddingTop: isDetailsPage ? '16px' : '0' }}>
      {event.status === 'cancelled' ? (
        <div role="status" style={{ minHeight: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 14px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', fontSize: '13px', fontWeight: '800' }}>
          此活動已取消
        </div>
      ) : <>
        {/* 已報名成功 badge removed as requested */}
        {isEventOwner ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => onEventEdit && onEventEdit(event)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                background: 'white',
                color: '#374151',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
              }}
            >
              <Edit2 style={{ width: '14px', height: '14px' }} />
              編輯活動
            </button>
            <button
              onClick={() => setShowCancelDialog(true)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '12px',
                border: 'none',
                background: '#dc2626',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.24)',
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#b91c1c';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.24)';
              }}
            >
              <Trash2 style={{ width: '14px', height: '14px' }} />
              取消活動
            </button>
          </div>
        ) : (event.participants && event.participants.includes(currentUserId)) ? (
          <button
            onClick={handleLeaveEvent}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '12px',
              border: 'none',
              background: isLoading ? '#d1d5db' : '#4e8bca',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              opacity: isLoading ? 0.7 : 1,
              boxShadow: isLoading ? 'none' : '0 4px 12px rgba(78, 139, 202, 0.2)',
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.background = '#376ba1';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(78, 139, 202, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.background = '#4e8bca';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(78, 139, 202, 0.2)';
              }
            }}
          >
            <X style={{ width: '14px', height: '14px' }} />
            {isLoading ? "退出中..." : "退出活動"}
          </button>
        ) : myStatus === 'pending' ? (
          <button
            onClick={() => {
              showConfirm('確認取消', '確定要取消報名嗎？\n\n取消後若想再次參加，需重新送出申請。', handleLeaveEvent, true);
            }}
            disabled={isLoading}
            style={{
              width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #9cc2e6',
              background: '#eef4f9', color: '#376ba1', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', letterSpacing: '0.02em', transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e0ecf7';
              e.target.style.borderColor = '#6a9fd3';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#eef4f9';
              e.target.style.borderColor = '#9cc2e6';
            }}
          >
            {isLoading ? '取消中...' : '審核中（點擊取消報名）'}
          </button>
        ) : myStatus === 'rejected' ? (
          <button disabled style={{
            width: '100%', padding: '10px', borderRadius: '12px',
            border: '1px solid #fca5a5', background: '#fef2f2',
            color: '#dc2626', fontSize: '13px', fontWeight: '600',
            cursor: 'not-allowed', letterSpacing: '0.02em'
          }}>
            報名未通過
          </button>
        ) : myStatus === 'approved' ? (
          <button
            onClick={handleLeaveEvent}
            disabled={isLoading}
            style={{
              width: '100%', padding: '10px', borderRadius: '12px', border: 'none',
              background: '#4e8bca', color: 'white', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(78, 139, 202, 0.2)', letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            <X style={{ width: '14px', height: '14px' }} />
            {isLoading ? '退出中...' : '退出活動'}
          </button>
        ) : totalParticipants >= (event.max_participants || Infinity) ? (
          <button
            disabled
            style={{
              width: '100%', padding: '10px', borderRadius: '12px', border: 'none',
              background: '#f3f4f6', color: '#9ca3af', fontSize: '13px', fontWeight: '600',
              cursor: 'not-allowed', letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            報名已額滿
          </button>
        ) : (
          <button
            onClick={handleJoin}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '12px',
              border: 'none',
              background: isLoading ? '#d1d5db' : sectionTheme.accent,
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              opacity: isLoading ? 0.7 : 1,
              boxShadow: isLoading ? 'none' : `0 4px 12px ${sectionTheme.accent}33`,
              letterSpacing: '0.02em'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.background = sectionTheme.accent;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 6px 16px ${sectionTheme.accent}55`;
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.background = sectionTheme.accent;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 12px ${sectionTheme.accent}33`;
              }
            }}
          >
            {isLoading ? "報名中..." : "我要參加"}
          </button>
        )}
      </>}
    </div>
  );

  // 桌面版活動詳情頁：左欄放圖片/說明/留言，右欄是 sticky 的報名資訊卡（時間地點人數 + 報名按鈕），
  // 滾動看留言時側欄仍固定在畫面上，避免使用者要一路滑到最底部才找得到報名按鈕
  if (isDetailsPage && isDesktop) {
    const commentsSection = (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
          <MessageCircle size={16} style={{ color: '#4e8bca' }} />
          <strong style={{ fontSize: '0.9rem', color: '#111827' }}>活動留言</strong>
          {comments.length > 0 && (
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4e8bca', background: '#eef4f9', borderRadius: '999px', padding: '2px 8px' }}>
              {comments.length}
            </span>
          )}
        </div>
        {comments.length === 0 ? (
          <p style={{ margin: '0 0 12px', color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center', padding: '12px 0' }}>
            還沒有人留言，快來搶先發表！
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {comments.map((comment, idx) => {
              const isOwn = comment.user_id === currentUserId;
              const isEditing = editingCommentId === comment.id;
              return (
                <div key={`${comment.id || idx}`} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, marginTop: '2px' }}>
                    <AvatarDisplay avatarId={comment.profiles?.avatar || 'avatar1'} size={28} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                      <span style={{ color: '#111827', fontWeight: 600, fontSize: '0.75rem' }}>
                        {comment.user_name || comment.profiles?.name || '匿名'}
                      </span>
                      {comment.created_at && (
                        <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>
                          {formatCommentTime(comment.created_at)}
                        </span>
                      )}
                      {isOwn && !isEditing && event.status !== 'cancelled' && (
                        <div style={{ display: 'flex', gap: '2px', marginLeft: 'auto' }}>
                          <button onClick={(e) => { e.stopPropagation(); setEditingCommentId(comment.id); setEditingContent(comment.content); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px', display: 'flex' }} aria-label="編輯留言"><Pencil size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: '2px', display: 'flex' }} aria-label="刪除留言"><Trash2 size={12} /></button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div onClick={e => e.stopPropagation()}>
                        <textarea value={editingContent} onChange={e => setEditingContent(e.target.value)} rows={2} autoFocus style={{ width: '100%', borderRadius: '10px', border: '1.5px solid #4e8bca', padding: '8px 10px', fontSize: '0.8rem', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                          <button onClick={() => { setEditingCommentId(null); setEditingContent(''); }} style={{ padding: '5px 14px', border: 'none', borderRadius: '999px', background: '#f3f4f6', fontSize: '12px', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}>取消</button>
                          <button onClick={() => handleEditComment(comment.id)} style={{ padding: '5px 14px', border: 'none', borderRadius: '999px', background: '#4e8bca', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>儲存</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'inline-block', background: isOwn ? '#eef4f9' : '#f3f4f6', borderRadius: '4px 14px 14px 14px', padding: '7px 12px', color: '#374151', fontSize: '0.8rem', lineHeight: '1.5', wordBreak: 'break-word', maxWidth: '100%' }}>
                        {comment.content}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            value={newComment}
            disabled={event.status === 'cancelled'}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleAddComment(); } }}
            placeholder={event.status === 'cancelled' ? '活動已取消，討論已關閉' : '留下你的討論...'}
            style={{ flex: 1, borderRadius: '999px', border: '1px solid #e5e7eb', padding: '9px 16px', fontSize: '0.85rem', outline: 'none', background: '#f9fafb', boxSizing: 'border-box' }}
            onFocus={(e) => { e.target.style.borderColor = '#4e8bca'; e.target.style.background = 'white'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }}
            onClick={(e) => e.stopPropagation()}
          />
          <button onClick={(e) => { e.stopPropagation(); handleAddComment(); }} disabled={event.status === 'cancelled' || !newComment.trim()} aria-label="送出留言" style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: event.status !== 'cancelled' && newComment.trim() ? '#4e8bca' : '#e5e7eb', color: 'white', cursor: event.status !== 'cancelled' && newComment.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s' }}>
            <Send size={15} style={{ marginLeft: '-2px', marginTop: '1px' }} />
          </button>
        </div>
      </div>
    );

    const cancelDialog = showCancelDialog && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 25px 60px rgba(0, 0, 0, 0.2)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px', textAlign: 'center' }}>確定要取消活動嗎？</h3>
          <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px', textAlign: 'center' }}>取消後活動會停止報名並移至歷史紀錄；參加者、留言與圖片仍會保留。</p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setShowCancelDialog(false)} style={{ flex: 1, padding: '12px 20px', borderRadius: '12px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>取消</button>
            <button onClick={handleCancelEvent} style={{ flex: 1, padding: '12px 20px', borderRadius: '12px', border: 'none', background: '#dc2626', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.24)' }}>確定取消</button>
          </div>
        </div>
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ background: 'white', borderRadius: '24px', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.02)', overflow: 'hidden', position: 'relative' }}>
            <button onClick={handleFavoriteToggle} style={{ position: 'absolute', top: '12px', right: '12px', background: isFavorited ? '#f59e0b' : 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', zIndex: 10 }}>
              <Star style={{ width: '16px', height: '16px', color: isFavorited ? 'white' : '#f59e0b', fill: isFavorited ? 'white' : 'none' }} />
            </button>
            <div style={{ position: 'absolute', top: '12px', left: '12px', right: '56px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', zIndex: 10 }}>
              {showTrendingBadge && (
                <span aria-label="近期熱門活動" title="近期熱門活動" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minHeight: '34px', padding: '6px 10px', color: '#ffffff', background: '#dc2626', borderRadius: '999px', fontSize: '13px', fontWeight: '800' }}>
                  <Flame size={16} fill="currentColor" aria-hidden="true" />{trendingLabel}
                </span>
              )}
              {showRecommendedBadge && (
                <span aria-label={isPartnerRecommendation ? '品牌合作活動' : '官方推薦活動'} title={isPartnerRecommendation ? '品牌合作活動' : '官方推薦活動'} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minHeight: '34px', padding: '6px 10px', color: isPartnerRecommendation ? '#f59e0b' : '#ffffff', background: isPartnerRecommendation ? '#fff4cc' : '#376ba1', border: isPartnerRecommendation ? '1px solid #fbbf24' : '1px solid transparent', borderRadius: '999px', boxShadow: isPartnerRecommendation ? '0 2px 8px rgba(245, 158, 11, 0.24)' : 'none', fontSize: '13px', fontWeight: '800' }}>
                  {isPartnerRecommendation ? <Handshake size={16} color="#f59e0b" aria-hidden="true" /> : <BadgeCheck size={16} aria-hidden="true" />}{recommendationLabel}
                </span>
              )}
              {(!hideCategoryTag || primaryTagOverride) && (
                <span style={getCardImageBadgeStyle(sectionTheme.accent, '14px')}>
                  {primaryTagOverride || event.category}
                </span>
              )}
              {event.subcategory && event.subcategory !== primaryTagOverride && (
                <span style={getCardImageBadgeStyle(sectionTheme.accent, '14px')}>
                  {event.subcategory}
                </span>
              )}
            </div>
            {event.image_url ? (
              <div style={{ height: '260px', backgroundImage: `url(${event.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3))' }} />
              </div>
            ) : (
              <div style={{ height: '90px', background: `linear-gradient(135deg, ${sectionTheme.tagBg}, #f4f8fb)`, position: 'relative' }} />
            )}
            <div style={{ padding: '24px' }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1f2937', margin: '0 0 10px' }}>{event.title}</h1>
              <p style={{ color: '#4b5563', fontSize: '15px', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>{event.description}</p>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '24px', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.02)', padding: '20px 24px' }}>
            {commentsSection}
          </div>
        </div>

        <div style={{ position: 'sticky', top: '92px', display: 'grid', gap: '16px' }}>
          <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.02)', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '14px', marginBottom: '12px' }}>
              <Calendar size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
              <span>{event.date} {formattedTime}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '14px' }}>
                <MapPin size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
                <span>{event.location}</span>
              </div>
              {event.address && (
                <div style={{ paddingLeft: '24px', color: '#9ca3af', fontSize: '12px' }}>{event.address}</div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ display: 'flex' }}>
                {event.organizer && (
                  <div style={{ marginLeft: 0, border: `2px solid ${sectionTheme.accent}`, borderRadius: '50%', background: 'white' }}>
                    <AvatarDisplay avatarId={event.organizer.profile?.avatar || 'avatar1'} size={28} />
                  </div>
                )}
                {event.participantDetails?.slice(0, 4).map((participant, index) => (
                  <div key={participant._id || index} style={{ marginLeft: '-8px', border: '2px solid white', borderRadius: '50%', background: 'white' }}>
                    <AvatarDisplay avatarId={participant.profile?.avatar || 'avatar1'} size={28} />
                  </div>
                ))}
                {event.participantDetails?.length > 4 && (
                  <div style={{ marginLeft: '-8px', width: '28px', height: '28px', borderRadius: '50%', border: '2px solid white', background: '#e5e7eb', color: '#4b5563', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    +{event.participantDetails.length - 4}
                  </div>
                )}
              </div>
              <span onClick={() => setShowParticipants(!showParticipants)} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#374151', fontSize: '13px', fontWeight: '600' }}>
                {totalParticipants}/{event.max_participants || '∞'}人參加
              </span>
            </div>

            {showParticipants && (
              <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px' }}>
                  {event.organizer && (
                    <div
                      onClick={() => handleUserClick(event.organizer)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px', backgroundColor: 'white', borderRadius: '8px', border: '2px solid #f59e0b', cursor: event.category === SPORT_CATEGORY ? 'default' : 'pointer' }}
                    >
                      <AvatarDisplay avatarId={event.organizer.profile?.avatar || 'avatar1'} size={26} />
                      <span style={{ fontSize: '10px', color: '#374151', marginTop: '3px', textAlign: 'center', fontWeight: '600' }}>{event.organizer.profile?.name || '主辦人'}（主辦）</span>
                    </div>
                  )}
                  {event.participantDetails?.map((participant, index) => (
                    <div
                      key={participant._id || index}
                      onClick={() => handleUserClick(participant)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: event.category === SPORT_CATEGORY ? 'default' : 'pointer' }}
                    >
                      <AvatarDisplay avatarId={participant.profile?.avatar || 'avatar1'} size={26} />
                      <span style={{ fontSize: '10px', color: '#374151', marginTop: '3px', textAlign: 'center' }}>{participant.profile?.name || '參與者'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: '10px', marginBottom: isEventOwner && pendingApplicants.length > 0 ? '12px' : '18px' }}>
              <AvatarDisplay avatarId={event.organizer?.profile?.avatar || 'avatar1'} size={24} />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>主辦：{event.organizer?.profile?.name || '主辦人'}</span>
            </div>

            {isEventOwner && pendingApplicants.length > 0 && (
              <div style={{ marginBottom: '18px', padding: '10px 12px', background: '#f6f9fc', borderRadius: '10px', border: '1px solid #6a9fd3' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#376ba1' }}>待審核報名（{pendingApplicants.length}人）</p>
                {pendingApplicants.map((applicant) => (
                  <div key={applicant.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AvatarDisplay avatarId={applicant.profiles?.avatar || 'avatar1'} size={26} />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{applicant.profiles?.name || '用戶'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => handleApproveParticipant(applicant.user_id, applicant.profiles?.name)} style={{ padding: '4px 12px', borderRadius: '8px', border: 'none', background: '#4e8bca', color: 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>同意</button>
                      <button onClick={() => handleRejectParticipant(applicant.user_id, applicant.profiles?.name)} style={{ padding: '4px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>婉拒</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {renderActionButtons()}
          </div>
        </div>

        {cancelDialog}
      </div>
    );
  }

  return (
    <motion.div
      onClick={(e) => {
        // SVG paths or specific element nesting sometimes lack a direct tag name block, so we use stopPropagation on the interactive buttons.
        if (!isDetailsPage && !['BUTTON', 'INPUT'].includes(e.target.tagName)) {
          navigate(`/events/${event._id}`);
        }
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!isDetailsPage ? { y: -6, boxShadow: '0 16px 48px rgba(0, 0, 0, 0.08)' } : {}}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{
        background: isDetailsPage ? 'transparent' : 'white',
        borderRadius: isDetailsPage ? '0' : '24px',
        boxShadow: isDetailsPage ? 'none' : '0 4px 24px rgba(0, 0, 0, 0.04)',
        border: isDetailsPage ? 'none' : '1px solid rgba(0, 0, 0, 0.02)',
        overflow: 'visible',
        cursor: isDetailsPage ? 'default' : 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: isDetailsPage ? '14px' : '0'
      }}
    >
      <div style={isDetailsPage ? {
        background: 'white',
        borderRadius: '24px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(0, 0, 0, 0.02)',
        overflow: 'hidden',
        position: 'relative'
      } : {}}>
        {/* Star Favorite Button - Top Right */}
        <button
          onClick={handleFavoriteToggle}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: isFavorited ? '#f59e0b' : 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s',
            zIndex: 10
          }}
        >
          <Star
            style={{
              width: '14px',
              height: '14px',
              color: isFavorited ? 'white' : '#f59e0b',
              fill: isFavorited ? 'white' : 'none'
            }}
          />
        </button>

        {/* Category and Favorite - Top Row */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          right: '52px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          zIndex: 10
        }}>
          {showTrendingBadge && (
            <span aria-label="近期熱門活動" title="近期熱門活動" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', minHeight: isDetailsPage ? '34px' : '28px', padding: isDetailsPage ? '6px 10px' : '5px 8px', flexShrink: 0, color: '#ffffff', background: '#dc2626', borderRadius: '999px', boxShadow: '0 2px 6px rgba(0,0,0,0.14)', fontSize: isDetailsPage ? '13px' : '11px', fontWeight: '800', lineHeight: 1 }}>
              <Flame size={isDetailsPage ? 16 : 13} fill="currentColor" aria-hidden="true" />
              {trendingLabel}
            </span>
          )}
          {showRecommendedBadge && (
            <span aria-label={isPartnerRecommendation ? '品牌合作活動' : '官方推薦活動'} title={isPartnerRecommendation ? '品牌合作活動' : '官方推薦活動'} style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              minHeight: isDetailsPage ? '34px' : '28px',
              padding: isDetailsPage ? '6px 10px' : '5px 8px',
              flexShrink: 0,
              borderRadius: '999px',
              background: isPartnerRecommendation ? '#fff4cc' : '#376ba1',
              color: isPartnerRecommendation ? '#f59e0b' : '#ffffff',
              border: isPartnerRecommendation ? '1px solid #fbbf24' : '1px solid transparent',
              boxShadow: isPartnerRecommendation ? '0 2px 8px rgba(245, 158, 11, 0.24)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
              fontSize: isDetailsPage ? '13px' : '11px',
              fontWeight: '800',
              lineHeight: 1
            }}>
              {isPartnerRecommendation
                ? <Handshake size={isDetailsPage ? 16 : 13} color="#f59e0b" aria-hidden="true" />
                : <BadgeCheck size={isDetailsPage ? 16 : 13} aria-hidden="true" />}
              {recommendationLabel}
            </span>
          )}
          {(!hideCategoryTag || primaryTagOverride) && (
            <span style={getCardImageBadgeStyle(sectionTheme.accent, isDetailsPage ? '14px' : '13px')}>
              {primaryTagOverride || event.category}
            </span>
          )}
          {event.subcategory && event.subcategory !== primaryTagOverride && (
            <span style={getCardImageBadgeStyle(sectionTheme.accent, isDetailsPage ? '14px' : '13px')}>
              {event.subcategory}
            </span>
          )}
          {comments.length > 0 && (
            <span style={{
              padding: '5px 10px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.95)',
              color: '#4b5563',
              fontSize: isDetailsPage ? '14px' : '13px',
              fontWeight: '600',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <MessageCircle size={14} />
              {comments.length}
            </span>
          )}
        </div>

        {/* Event Image or Gradient Header */}
        {event.image_url ? (
          <div style={{
            height: isDetailsPage ? '100px' : '180px',
            backgroundImage: `url(${event.image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3))'
            }} />
          </div>
        ) : (
          <div style={{
            height: isDetailsPage ? '48px' : '100px',
            background: `linear-gradient(135deg, ${sectionTheme.tagBg}, #f4f8fb)`,
            position: 'relative'
          }} />
        )}

        {/* Content */}
        <div style={{ padding: isDetailsPage ? '20px 24px 24px' : '12px 20px 16px' }}>
          {/* Title and Date */}
          <div style={{ marginBottom: isDetailsPage ? '16px' : '10px' }}>
            <h3 style={{
              fontSize: isDetailsPage ? '1.4rem' : '1rem',
              fontWeight: isDetailsPage ? '800' : '700',
              color: '#1f2937',
              margin: '0 0 8px 0',
              lineHeight: '1.3',
              overflow: isDetailsPage ? 'visible' : 'hidden',
              display: isDetailsPage ? 'block' : '-webkit-box',
              WebkitLineClamp: isDetailsPage ? 'none' : 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {event.title}
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#6b7280',
              fontSize: isDetailsPage ? '14px' : '12px'
            }}>
              <Calendar style={{ width: isDetailsPage ? '14px' : '12px', height: isDetailsPage ? '14px' : '12px' }} />
              <span>{event.date} {formattedTime}</span>
            </div>
          </div>

          {/* Description */}
          <p style={{
            color: '#4b5563',
            fontSize: isDetailsPage ? '15px' : '13px',
            lineHeight: '1.6',
            marginBottom: isDetailsPage ? '16px' : '10px',
            overflow: isDetailsPage ? 'visible' : 'hidden',
            display: isDetailsPage ? 'block' : '-webkit-box',
            WebkitLineClamp: isDetailsPage ? 'none' : 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {event.description}
          </p>

          {/* Location and Participants */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isDetailsPage ? '8px' : '6px', marginBottom: isDetailsPage ? '20px' : '12px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: isDetailsPage ? '14px' : '12px', minWidth: 0 }}>
              <MapPin style={{ width: isDetailsPage ? '14px' : '12px', height: isDetailsPage ? '14px' : '12px', flexShrink: 0 }} />
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {event.location}
              </span>
            </div>
            {event.address && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#9ca3af',
                fontSize: '11px',
                paddingLeft: '16px',
                minWidth: 0
              }}>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {event.address}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', fontSize: '12px' }}>
              <Users style={{ width: '12px', height: '12px' }} />
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setShowParticipants(!showParticipants)}
              >
                {totalParticipants}/{event.max_participants || '∞'}人參加
              </span>
            </div>
          </div>

          {/* Phase 2.1: 頭像堆疊 — 主辦人 + 已核准參與者，僅列表卡片顯示 */}
          {!isDetailsPage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ display: 'flex' }}>
                {event.organizer && (
                  <div style={{ marginLeft: 0, border: `2px solid ${sectionTheme.accent}`, borderRadius: '50%', background: 'white' }}>
                    <AvatarDisplay avatarId={event.organizer.profile?.avatar || 'avatar1'} size={24} />
                  </div>
                )}
                {event.participantDetails?.slice(0, 3).map((participant, index) => (
                  <div
                    key={participant._id || index}
                    style={{ marginLeft: '-8px', border: '2px solid white', borderRadius: '50%', background: 'white' }}
                  >
                    <AvatarDisplay avatarId={participant.profile?.avatar || 'avatar1'} size={24} />
                  </div>
                ))}
                {event.participantDetails?.length > 3 && (
                  <div style={{
                    marginLeft: '-8px', width: '24px', height: '24px', borderRadius: '50%',
                    border: '2px solid white', background: '#e5e7eb', color: '#4b5563',
                    fontSize: '10px', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    +{event.participantDetails.length - 3}
                  </div>
                )}
              </div>
              {(!event.participantDetails || event.participantDetails.length === 0) && (
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  找{event.max_participants ? `${Math.max(event.max_participants - totalParticipants, 0)} ` : ''}人
                </span>
              )}
            </div>
          )}

          {/* Organizer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            marginBottom: '12px'
          }}>
            <AvatarDisplay avatarId={event.organizer?.profile?.avatar || 'avatar1'} size={20} />
            <span style={{ fontSize: '11px', color: '#6b7280' }}>
              主辦：{event.organizer?.profile?.name || '主辦人'}
            </span>
          </div>

          {/* Participants List */}
          {showParticipants && (
            <div style={{
              marginBottom: '12px',
              padding: '10px',
              backgroundColor: '#f8fafc',
              borderRadius: '6px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                margin: '0 0 8px 0'
              }}>
                參與者 ({totalParticipants}人)
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '6px'
              }}>
                {/* Show organizer first */}
                {event.organizer && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '6px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      cursor: event.category === SPORT_CATEGORY ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      border: '2px solid #f59e0b'
                    }}
                    onClick={() => handleUserClick(event.organizer)}
                    onMouseEnter={(e) => {
                      if (event.category === SPORT_CATEGORY) return;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (event.category === SPORT_CATEGORY) return;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <AvatarDisplay avatarId={event.organizer.profile?.avatar || 'avatar1'} size={28} />
                    <span style={{
                      fontSize: '10px',
                      color: '#374151',
                      marginTop: '3px',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}>
                      {event.organizer.profile?.name || '主辦人'} (主辦)
                    </span>
                  </div>
                )}

                {/* Show other participants */}
                {event.participantDetails?.map((participant, index) => (
                  <div
                    key={participant._id || index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '6px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      cursor: event.category === SPORT_CATEGORY ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid #e2e8f0'
                    }}
                    onClick={() => handleUserClick(participant)}
                    onMouseEnter={(e) => {
                      if (event.category === SPORT_CATEGORY) return;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (event.category === SPORT_CATEGORY) return;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <AvatarDisplay avatarId={participant.profile?.avatar || 'avatar1'} size={28} />
                    <span style={{
                      fontSize: '10px',
                      color: '#374151',
                      marginTop: '3px',
                      textAlign: 'center'
                    }}>
                      {participant.profile?.name || '參與者'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Organizer: Pending Approvals Panel */}
          {isEventOwner && pendingApplicants.length > 0 && (
            <div style={{
              marginBottom: '12px', padding: '10px 12px',
              background: '#f6f9fc', borderRadius: '10px', border: '1px solid #6a9fd3'
            }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#376ba1' }}>
                待審核報名 ({pendingApplicants.length}人)
              </p>
              {pendingApplicants.map((applicant) => (
                <div key={applicant.user_id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 0', borderBottom: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AvatarDisplay avatarId={applicant.profiles?.avatar || 'avatar1'} size={28} />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      {applicant.profiles?.name || '用戶'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleApproveParticipant(applicant.user_id, applicant.profiles?.name)}
                      style={{
                        padding: '4px 12px', borderRadius: '8px', border: 'none',
                        background: '#4e8bca', color: 'white', fontSize: '12px',
                        fontWeight: '600', cursor: 'pointer'
                      }}
                    >同意</button>
                    <button
                      onClick={() => handleRejectParticipant(applicant.user_id, applicant.profiles?.name)}
                      style={{
                        padding: '4px 12px', borderRadius: '8px', border: '1px solid #e5e7eb',
                        background: 'white', color: '#ef4444', fontSize: '12px',
                        fontWeight: '600', cursor: 'pointer'
                      }}
                    >婉拒</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Button for non-details page */}
          {!isDetailsPage && renderActionButtons()}
        </div>
      </div>

      {isDetailsPage && (
        <div style={{
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.02)',
          padding: '20px 24px',
          position: 'relative'
        }}>
          {/* Comments Section - Only show on Details Page */}
          <div style={{ marginBottom: '12px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
              <MessageCircle size={16} style={{ color: '#4e8bca' }} />
              <strong style={{ fontSize: '0.9rem', color: '#111827' }}>活動留言</strong>
              {comments.length > 0 && (
                <span style={{
                  fontSize: '0.7rem', fontWeight: 600, color: '#4e8bca',
                  background: '#eef4f9', borderRadius: '999px', padding: '2px 8px'
                }}>
                  {comments.length}
                </span>
              )}
            </div>

            {/* Comment list */}
            {comments.length === 0 ? (
              <p style={{ margin: '0 0 12px', color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center', padding: '12px 0' }}>
                還沒有人留言，快來搶先發表！
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                {comments.map((comment, idx) => {
                  const isOwn = comment.user_id === currentUserId;
                  const isEditing = editingCommentId === comment.id;
                  return (
                    <div key={`${comment.id || idx}`} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flexShrink: 0, marginTop: '2px' }}>
                        <AvatarDisplay avatarId={comment.profiles?.avatar || 'avatar1'} size={28} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                          <span style={{ color: '#111827', fontWeight: 600, fontSize: '0.75rem' }}>
                            {comment.user_name || comment.profiles?.name || '匿名'}
                          </span>
                          {comment.created_at && (
                            <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>
                              {formatCommentTime(comment.created_at)}
                            </span>
                          )}
                          {isOwn && !isEditing && event.status !== 'cancelled' && (
                            <div style={{ display: 'flex', gap: '2px', marginLeft: 'auto' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingCommentId(comment.id); setEditingContent(comment.content); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px', display: 'flex' }}
                                aria-label="編輯留言"
                              ><Pencil size={12} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment.id); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: '2px', display: 'flex' }}
                                aria-label="刪除留言"
                              ><Trash2 size={12} /></button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div onClick={e => e.stopPropagation()}>
                            <textarea
                              value={editingContent}
                              onChange={e => setEditingContent(e.target.value)}
                              rows={2}
                              autoFocus
                              style={{ width: '100%', borderRadius: '10px', border: '1.5px solid #4e8bca', padding: '8px 10px', fontSize: '0.8rem', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                            />
                            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => { setEditingCommentId(null); setEditingContent(''); }}
                                style={{ padding: '5px 14px', border: 'none', borderRadius: '999px', background: '#f3f4f6', fontSize: '12px', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
                              >取消</button>
                              <button
                                onClick={() => handleEditComment(comment.id)}
                                style={{ padding: '5px 14px', border: 'none', borderRadius: '999px', background: '#4e8bca', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                              >儲存</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            display: 'inline-block',
                            background: isOwn ? '#eef4f9' : '#f3f4f6',
                            borderRadius: '4px 14px 14px 14px',
                            padding: '7px 12px',
                            color: '#374151',
                            fontSize: '0.8rem',
                            lineHeight: '1.5',
                            wordBreak: 'break-word',
                            maxWidth: '100%'
                          }}>
                            {comment.content}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Input row */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                value={newComment}
                disabled={event.status === 'cancelled'}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleAddComment(); } }}
                placeholder={event.status === 'cancelled' ? '活動已取消，討論已關閉' : '留下你的討論...'}
                style={{
                  flex: 1, borderRadius: '999px', border: '1px solid #e5e7eb',
                  padding: '9px 16px', fontSize: '0.85rem', outline: 'none',
                  background: '#f9fafb', boxSizing: 'border-box'
                }}
                onFocus={(e) => { e.target.style.borderColor = '#4e8bca'; e.target.style.background = 'white'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => { e.stopPropagation(); handleAddComment(); }}
                disabled={event.status === 'cancelled' || !newComment.trim()}
                aria-label="送出留言"
                style={{
                  width: '36px', height: '36px', borderRadius: '50%', border: 'none',
                  background: event.status !== 'cancelled' && newComment.trim() ? '#4e8bca' : '#e5e7eb',
                  color: 'white', cursor: event.status !== 'cancelled' && newComment.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.2s'
                }}
              >
                <Send size={15} style={{ marginLeft: '-2px', marginTop: '1px' }} />
              </button>
            </div>
          </div>
          {isDetailsPage && renderActionButtons()}
        </div>
      )}

      {/* Cancel Event Confirmation Dialog */}
      {showCancelDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              確定要取消活動嗎？
            </h3>
            <p style={{
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              取消後活動會停止報名並移至歷史紀錄；參加者、留言與圖片仍會保留。
            </p>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowCancelDialog(false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                }}
              >
                取消
              </button>
              <button
                onClick={handleCancelEvent}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#dc2626',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.24)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#b91c1c';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#dc2626';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.24)';
                }}
              >
                確定取消
              </button>
            </div>
          </div>
        </div>
      )}
      <LoginPromptModal isOpen={isLoginPromptOpen} onClose={() => setIsLoginPromptOpen(false)} />
    </motion.div>
  );
}
